import type { RequestDetailViewState } from "./request-detail-view-types";
import { dateTime, dateOnly, remainingDayLabel } from "./request-detail-presentation";
import { AlertTriangle, Send } from "lucide-react";
import { can } from "../../../../../shared/auth/access";
import { ProcurementFlow } from "../../../../../shared/components/ProcurementFlow";
import { RfqWorkspace } from "../../../RfqWorkspace";

type Props = Pick<
  RequestDetailViewState,
  | "notify"
  | "user"
  | "presentation"
  | "detail"
  | "routeForApproval"
  | "requestDecisionBusy"
  | "beginEditing"
  | "activeCorrection"
  | "detailFlowSteps"
  | "specialistFlowPresentation"
  | "canRouteRequest"
  | "canEditRequest"
>;
export function RequestOverview({
  notify,
  user,
  presentation,
  detail,
  routeForApproval,
  requestDecisionBusy,
  beginEditing,
  activeCorrection,
  detailFlowSteps,
  specialistFlowPresentation,
  canRouteRequest,
  canEditRequest,
}: Props) {
  return <section className="request-detail-overview">
        {presentation === "page" || canRouteRequest || canEditRequest ? (
          <header
            className={`request-detail-header${presentation === "workspace" ? " is-actions-only" : ""}`}
          >
            {presentation === "page" ? (
              <div className="request-detail-copy">
                <div className="request-detail-meta">
                  <span className="section-kicker">{detail.id}</span>
                  <span className="request-priority">
                    {detail.priority} öncelik
                  </span>
                </div>
                <h1>{detail.title}</h1>
                <p>{detail.description}</p>
              </div>
            ) : null}
            {canRouteRequest || canEditRequest ? (
              <div className="request-detail-actions">
                {canRouteRequest ? (
                  <button className="approve-button" disabled={requestDecisionBusy} onClick={routeForApproval}>
                    <Send size={15} /> Onaya gönder
                  </button>
                ) : null}
                {canEditRequest ? (
                  <button className="secondary-button" onClick={beginEditing}>
                    Talebi düzenle
                  </button>
                ) : null}
              </div>
            ) : null}
          </header>
        ) : null}

        <ProcurementFlow
          stage={detail.status}
          steps={detailFlowSteps}
          presentation={specialistFlowPresentation}
          focusRole={
            user.role === "SATIN_ALMA_MUDURU" || user.role === "GENEL_MUDUR"
              ? "GENEL_MUDUR"
              : "SATIN_ALMA_MUDURU"
          }
        />
        {can(user,"rfq.read")?<RfqWorkspace requestNo={detail.id} title={detail.title} category={detail.category} quantity={detail.quantity} unit={detail.unit} neededAt={detail.neededAt} user={user} notify={notify}/>:null}

        {activeCorrection ? (
          <section
            className="active-correction-instruction"
            aria-labelledby={`active-correction-${activeCorrection.id}`}
          >
            <AlertTriangle size={19} aria-hidden="true" />
            <div className="active-correction-copy">
              <span>Aktif düzeltme talimatı</span>
              <strong id={`active-correction-${activeCorrection.id}`}>
                {activeCorrection.decision_note}
              </strong>
              {activeCorrection.revision_fields?.length ? (
                <p>
                  İlgili konular: {activeCorrection.revision_fields.join(", ")}
                </p>
              ) : null}
            </div>
            <small>
              {activeCorrection.decided_by ?? "Satın Alma Müdürü"}
              {activeCorrection.decided_at
                ? ` · ${dateTime(activeCorrection.decided_at)}`
                : ""}
            </small>
          </section>
        ) : null}

        <div className="detail-facts">
          <div>
            <small>Talep miktarı</small>
            <strong>
              {detail.quantity.toLocaleString("tr-TR")} {detail.unit}
            </strong>
          </div>
          <div>
            <small>İhtiyaç tarihi</small>
            <strong>{dateOnly(detail.neededAt)}</strong>
          </div>
          <div>
            <small>Kalan süre</small>
            <strong>{remainingDayLabel(detail.neededAt)}</strong>
          </div>
          <div>
            <small>Talep eden</small>
            <strong>{detail.requester}</strong>
          </div>
          <div>
            <small>Departman</small>
            <strong>{detail.department}</strong>
          </div>
          <div>
            <small>Kategori</small>
            <strong>{detail.category}</strong>
          </div>
        </div>
      </section>;
}
